import { Button } from '@consigli/facade';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiClock, FiUsers, FiRepeat, FiTrash2 } from 'react-icons/fi';
import { MdDescription } from 'react-icons/md';

import { TranscriptStatusBadge } from './components/transcript-status-badge';

import type { TranscriptStatus } from './utils/transcript-status';

export const MeetingCard = ({
  meetingId,
  title,
  company,
  date,
  time,
  participants,
  summary,
  flag,
  flagAction,
  onSelect,
  isRecurring,
  onDelete,
  transcriptStatus,
}: {
  meetingId?: string;
  title: string;
  company?: string;
  date: string;
  time: string;
  participants: string;
  summary?: string;
  flag?: string;
  flagAction?: string;
  onSelect?: () => void;
  isRecurring?: boolean;
  onDelete?: () => void;
  transcriptStatus?: TranscriptStatus;
}) => {
  const { t } = useTranslation();

  const handleCardClick = () => {
    if (meetingId && onSelect) {
      onSelect();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div
      className="group bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 ease-in-out cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
      onClick={handleCardClick}
      onKeyDown={handleKeyPress}
      role="button"
      tabIndex={0}
      aria-label={`View details for meeting: ${title}`}
    >
      <div className="mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            {isRecurring && (
              <FiRepeat
                size={16}
                className="text-gray-600"
                title={t('meeting-secretary.meetings.recurring-series')}
              />
            )}
          </div>
          {onDelete && (
            <Button
              iconOnly
              icon={FiTrash2}
              tertiary
              rounded
              onClick={handleDeleteClick}
              title={t('meeting-secretary.meetings.delete-meeting')}
              aria-label={t('meeting-secretary.meetings.delete-meeting')}
              iconSize={18}
              className="text-gray-400 hover:text-gray-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 max-md:opacity-100"
            />
          )}
        </div>
        <p className="text-gray-600 font-medium">{company}</p>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap text-sm text-gray-600 mb-4">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-1">
            <FiCalendar size={16} />
            <span>{date}</span>
          </div>
          <div className="flex items-center gap-1">
            <FiClock size={16} />
            <span>{time}</span>
          </div>
          <div className="flex items-center gap-1">
            <FiUsers size={16} />
            <span>{participants}</span>
          </div>
        </div>
        <TranscriptStatusBadge status={transcriptStatus ?? null} />
      </div>

      {summary && <p className="text-gray-700 mb-4 leading-relaxed">{summary}</p>}

      {flag && flagAction && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`text-white text-xs font-semibold px-3 py-1 rounded-full ${
                flag === 'Contract Issue'
                  ? 'bg-[hsl(0,84%,60%)]'
                  : 'bg-yellow-50 border border-yellow-400 text-yellow-800'
              }`}
            >
              {flag}
            </span>
            <button
              className={`text-gray-800 text-xs font-semibold px-3 py-1 flex items-center gap-1 ${
                flagAction === 'See in contract'
                  ? 'bg-[hsl(210,40%,98%)] border border-[hsl(214,32%,91%)] rounded-md'
                  : 'bg-blue-50 border border-blue-400 text-blue-700 rounded-full'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <MdDescription size={12} />
              {flagAction}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
